import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import { gameService } from '../../services/gameService';
import { User } from '../../types';
import CrimePanel from './CrimePanel';
import GTA from './GTA';
import Travel from './Travel';
import Garage from './Garage';
import Drugs from './Drugs';
import UsersOnline from './UsersOnline';
import JailPage from './JailPage';
import Blackjack from './Blackjack';
import Roulette from './Roulette';
import Dealership from './Dealership';
import Chase from './Chase';
import GameLayout from './GameLayout';
import GameErrorBoundary from '../ErrorBoundary';

const Dashboard: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState<'main' | 'gta' | 'travel' | 'garage' | 'drugs' | 'crimes' | 'users-online' | 'jail' | 'blackjack' | 'roulette' | 'dealership' | 'chase'>('main');
  const navigate = useNavigate();

  const loadUserData = useCallback(async () => {
    try {
      const storedUser = authService.getStoredUser();
      if (storedUser) {
        console.log('🔄 Dashboard: Loading fresh user data...');
        const userStats = await gameService.refreshUserStats();
        console.log('📊 Dashboard: User data refreshed:', {
          lastGTATime: userStats.user.lastGTATime ? '✅ Set' : '❌ Not set',
          lastGTAType: userStats.user.lastGTAType || 'None',
          money: '$' + userStats.user.money.toLocaleString(),
          garageCars: userStats.user.garageCars.length + ' cars'
        });
        setUser(userStats.user);
      } else {
        navigate('/login');
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  const handleCrimeComplete = useCallback(() => {
    loadUserData(); // Refresh user stats after crime
  }, [loadUserData]);

  const handleUserUpdate = useCallback((updates: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...updates });
    }
  }, [user]);

  const handleLogout = useCallback(() => {
    authService.logout();
    navigate('/login');
  }, [navigate]);

  const handleMenuClick = useCallback((page: 'main' | 'gta' | 'travel' | 'garage' | 'drugs' | 'crimes' | 'users-online' | 'jail' | 'blackjack' | 'roulette' | 'dealership' | 'chase') => {
    setCurrentPage(page);
  }, []);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-card">
          <h3>Loading...</h3>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="error-container">
        <div className="error-card">
          <h3>Error loading user data</h3>
          <button className="btn mt-2" onClick={() => navigate('/login')}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const renderMainContent = () => {
    switch (currentPage) {
      case 'gta':
        return <GTA user={user} onUpdate={loadUserData} />;
      case 'travel':
        return <Travel user={user} onUpdate={loadUserData} />;
      case 'garage':
        return <Garage user={user} onUpdate={loadUserData} onUserUpdate={handleUserUpdate} />;
      case 'drugs':
        return <Drugs user={user} onUpdate={loadUserData} />;
      case 'crimes':
        return <CrimePanel user={user} onCrimeComplete={handleCrimeComplete} />;
      case 'users-online':
        return <UsersOnline />;
      case 'jail':
        return <JailPage user={user} onUpdate={loadUserData} />;
      case 'blackjack':
        return <Blackjack user={user} onUpdate={loadUserData} />;
      case 'roulette':
        return <Roulette user={user} onUpdate={loadUserData} />;
      case 'dealership':
        return <Dealership user={user} onUserUpdate={handleUserUpdate} onUpdate={loadUserData} />;
      case 'chase':
        return <Chase user={user} onUserUpdate={handleUserUpdate} onUpdate={loadUserData} />;
      default:
        return (
          <div className="main-dashboard">
            <div className="dashboard-header">
              <h1>Welcome to the Streets</h1>
              <p>Choose your path, {user.username}...</p>
            </div>
            <div className="dashboard-grid">
              <div className="dashboard-card" onClick={() => setCurrentPage('crimes')}>
                <div className="card-icon">⚔️</div>
                <h3>Crimes</h3>
                <p>Commit various crimes to earn money and experience</p>
              </div>
              <div className="dashboard-card" onClick={() => setCurrentPage('gta')}>
                <div className="card-icon">🚗</div>
                <h3>Grand Theft Auto</h3>
                <p>Steal cars and build your collection</p>
              </div>
              <div className="dashboard-card" onClick={() => setCurrentPage('chase')}>
                <div className="card-icon">🚔</div>
                <h3>Chase</h3>
                <p>Escape from police in high-speed pursuits</p>
              </div>
              <div className="dashboard-card" onClick={() => setCurrentPage('travel')}>
                <div className="card-icon">🗺️</div>
                <h3>Travel</h3>
                <p>Move between different locations</p>
              </div>
              <div className="dashboard-card" onClick={() => setCurrentPage('garage')}>
                <div className="card-icon">🏢</div>
                <h3>Garage</h3>
                <p>Manage your stolen vehicles</p>
              </div>
              <div className="dashboard-card" onClick={() => setCurrentPage('dealership')}>
                <div className="card-icon">🏪</div>
                <h3>Dealership</h3>
                <p>Buy, sell, and manage cars in the automotive business</p>
              </div>
              <div className="dashboard-card" onClick={() => setCurrentPage('drugs')}>
                <div className="card-icon">💊</div>
                <h3>Drugs</h3>
                <p>Buy, sell, and trade drugs</p>
              </div>
              <div className="dashboard-card" onClick={() => setCurrentPage('users-online')}>
                <div className="card-icon">👥</div>
                <h3>Users Online</h3>
                <p>See who's currently active in the game</p>
              </div>
              <div className="dashboard-card" onClick={() => setCurrentPage('jail')}>
                <div className="card-icon">🚔</div>
                <h3>Jail</h3>
                <p>Bust out other players or set bust rewards</p>
              </div>
              <div className="dashboard-card" onClick={() => setCurrentPage('blackjack')}>
                <div className="card-icon">🎰</div>
                <h3>Blackjack</h3>
                <p>Play blackjack at local casinos</p>
              </div>
              <div className="dashboard-card" onClick={() => setCurrentPage('roulette')}>
                <div className="card-icon">🎲</div>
                <h3>Roulette</h3>
                <p>Spin the wheel and place your bets</p>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <GameErrorBoundary>
      <GameLayout 
        user={user} 
        onMenuClick={handleMenuClick} 
        onLogout={handleLogout}
      >
        {renderMainContent()}
      </GameLayout>
    </GameErrorBoundary>
  );
};

export default Dashboard;
